import React, { useState } from "react";
import SwoodleLogo from "../../Assets/Icons/SwoodleUpdatedLogo.svg";
import { NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "./LoginHeader.scss";

const LoginHeader = ({ isFixedLayout = false }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const navItems = [
        { label: "Members", href: "https://swoodle.com.au/members/" },
        { label: "Merchants", href: "https://swoodle.com.au/merchant-feature/" },
        { label: "Fundraisers", href: "https://swoodle.com.au/fundraisers/" },
        { label: "Contact Us", href: "https://swoodle.com.au/contact-us/" },
    ];

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    return (
        <header className={`relative px-4 md:px-8 flex items-center justify-between gap-4 shadow-lg border-none outline-none z-[1000] ${isFixedLayout ? "bg-transparent py-2 w-full" : "bg-[#002042] py-4 md:py-6 rounded-[20px]"}`}>
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
                <a href="https://swoodle.com.au/" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <img
                        src={SwoodleLogo}
                        alt="Swoodle"
                        className="w-[110px] sm:w-[130px] md:w-[170px] lg:w-[185px] h-auto"
                    />
                </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-between flex-1 min-w-0 gap-2 xl:gap-6">
                <nav className="flex items-center flex-1 min-w-0 mr-1 xl:mr-2">
                    {navItems.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="header-btn-slide header-nav-link text-[13px] xl:text-[16px] 2xl:text-[18px] font-poppins font-[500] px-2 xl:px-4 2xl:px-5 py-2 rounded-full whitespace-nowrap"
                        >
                            <span>{item.label}</span>
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-1 xl:gap-3 flex-shrink-0">
                    <NavLink
                        to="/merchant/getting-started"
                        onClick={() => window.scrollTo(0, 0)}
                        className="header-btn-yellow-slide bg-[#00ADEF] text-white px-[14px] xl:px-[20px] 2xl:px-[25px] py-[12px] xl:py-[14px] 2xl:py-[16px] rounded-full text-[12px] xl:text-[14px] 2xl:text-[16px] font-poppins font-[500] whitespace-nowrap shadow-md text-center"
                    >
                        <span>Become a Merchant</span>
                    </NavLink>
                    <NavLink
                        to="/fundraiser/getting-started"
                        onClick={() => window.scrollTo(0, 0)}
                        className="header-btn-yellow-slide bg-[#00ADEF] text-white px-[14px] xl:px-[20px] 2xl:px-[25px] py-[12px] xl:py-[14px] 2xl:py-[16px] rounded-full text-[12px] xl:text-[14px] 2xl:text-[16px] font-poppins font-[500] whitespace-nowrap shadow-md text-center"
                    >
                        <span>Become a Fundraiser</span>
                    </NavLink>
                    <NavLink
                        to="/member-setup"
                        onClick={() => window.scrollTo(0, 0)}
                        className="header-btn-black-slide bg-[#FFCA08] text-[#303030] px-[16px] xl:px-[24px] 2xl:px-[30px] py-[12px] xl:py-[14px] 2xl:py-[16px] rounded-full text-[12px] xl:text-[14px] 2xl:text-[16px] font-poppins font-[600] whitespace-nowrap shadow-md text-center"
                    >
                        <span>Join Now</span>
                    </NavLink>
                </div>
            </div>

            {/* Mobile Menu Icon */}
            <button
                onClick={toggleMenu}
                className="lg:hidden text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Toggle menu"
            >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

            {/* Mobile Sidebar Overlay */}
            <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] lg:hidden transition-opacity duration-300 ${isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={toggleMenu}>
                <div
                    className={`absolute right-0 top-0 h-full w-[80%] sm:w-[60%] bg-[#002042] p-6 shadow-2xl transition-transform duration-300 transform ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex justify-end mb-8">
                        <button onClick={toggleMenu} className="text-white p-2">
                            <X size={32} />
                        </button>
                    </div>

                    <nav className="flex flex-col gap-4 mb-8">
                        {navItems.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white text-[18px] font-poppins font-[500] py-3 border-b border-white/10 hover:text-[#FFCA08] transition-colors"
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex flex-col gap-4">
                        <NavLink
                            to="/merchant/getting-started"
                            className="bg-[#00ADEF] text-white py-4 rounded-full text-[16px] font-poppins font-[500] text-center shadow-lg active:scale-95 transition-transform"
                            onClick={() => { toggleMenu(); window.scrollTo(0, 0); }}
                        >
                            Become a Merchant
                        </NavLink>
                        <NavLink
                            to="/fundraiser/getting-started"
                            className="bg-[#00ADEF] text-white py-4 rounded-full text-[16px] font-poppins font-[500] text-center shadow-lg active:scale-95 transition-transform"
                            onClick={() => { toggleMenu(); window.scrollTo(0, 0); }}
                        >
                            Become a Fundraiser
                        </NavLink>
                        <NavLink
                            to="/member-setup"
                            className="bg-[#FFCA08] text-[#303030] py-4 rounded-full text-[16px] font-poppins font-[600] text-center shadow-lg active:scale-95 transition-transform"
                            onClick={() => { toggleMenu(); window.scrollTo(0, 0); }}
                        >
                            Join Now
                        </NavLink>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default LoginHeader;
